<div class="container mx-auto">
  <!-- Section Header -->
  <div
    class="flex flex-wrap items-center justify-between mb-6 pb-4 border-b border-base-300"
  >
    <div class="flex-1">
      <h2
        class="text-2xl font-bold text-base-content flex items-center gap-2"
      >
        <i class="fa-solid fa-graduation-cap text-primary"></i>
        <span>Course Management</span>
      </h2>
      <p class="text-sm text-base-content/60 mt-1">
        Manage your self-paced courses, structured curriculum, and student content
      </p>
    </div>
    <div class="flex items-center gap-2">
      <button
        type="button"
        class="daisy-btn daisy-btn-outline gap-2"
        (click)="openInviteModal()"
      >
        <i class="fa-solid fa-user-plus"></i>
        <span class="hidden sm:inline">Invite Students</span>
      </button>
      <button
        class="daisy-btn daisy-btn-primary gap-2"
        (click)="createCourse()"
      >
        <i class="fa-solid fa-plus"></i>
        <span class="hidden sm:inline">Create New Course</span>
      </button>
    </div>
  </div>

  <!-- Filters & Search Toolbar -->
  <div
    class="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-4 mb-8"
  >
    <!-- Status Filter Pills -->
    <div class="daisy-join flex-wrap">
      <button
        type="button"
        class="daisy-btn daisy-btn-sm daisy-join-item"
        [class.daisy-btn-active]="selectedStatus === 'ALL'"
        [class.daisy-btn-primary]="selectedStatus === 'ALL'"
        (click)="onStatusChange('ALL')"
      >
        All ({{ courses.length }})
      </button>
      <button
        type="button"
        class="daisy-btn daisy-btn-sm daisy-join-item"
        [class.daisy-btn-active]="selectedStatus === 'PUBLISHED'"
        [class.daisy-btn-primary]="selectedStatus === 'PUBLISHED'"
        (click)="onStatusChange('PUBLISHED')"
      >
        Published
      </button>
      <button
        type="button"
        class="daisy-btn daisy-btn-sm daisy-join-item"
        [class.daisy-btn-active]="selectedStatus === 'UNDER_REVIEW'"
        [class.daisy-btn-primary]="selectedStatus === 'UNDER_REVIEW'"
        (click)="onStatusChange('UNDER_REVIEW')"
      >
        Under Review
      </button>
      <button
        type="button"
        class="daisy-btn daisy-btn-sm daisy-join-item"
        [class.daisy-btn-active]="selectedStatus === 'DRAFT'"
        [class.daisy-btn-primary]="selectedStatus === 'DRAFT'"
        (click)="onStatusChange('DRAFT')"
      >
        Drafts
      </button>
    </div>

    <!-- Search Input -->
    <div class="w-full sm:w-72">
      <div class="relative">
        <input
          #searchInput
          type="text"
          placeholder="Search by title, subject..."
          [value]="searchQuery"
          (input)="onSearch(searchInput.value)"
          class="daisy-input daisy-input-bordered daisy-input-sm w-full pl-9"
        />
        <i
          class="fa-solid fa-magnifying-glass absolute left-3 top-1/2 -translate-y-1/2 text-base-content/40 text-xs"
        ></i>
      </div>
    </div>
  </div>

  <!-- Loading Skeletons -->
  @if (isLoading) {
    <div class="grid gap-8 md:grid-cols-2 lg:grid-cols-3">
      @for (i of [1, 2, 3]; track i) {
        <div
          class="bg-base-100 border border-base-300 rounded-2xl shadow-xl overflow-hidden flex flex-col h-full"
        >
          <div class="daisy-skeleton h-48 w-full rounded-none"></div>
          <div class="p-6 flex-1 flex flex-col justify-between gap-4">
            <div class="space-y-2">
              <div class="daisy-skeleton h-6 w-3/4"></div>
              <div class="daisy-skeleton h-4 w-full"></div>
              <div class="daisy-skeleton h-4 w-2/3"></div>
            </div>
            <div class="flex gap-3 pt-2">
              <div class="daisy-skeleton h-10 flex-1 rounded-lg"></div>
              <div class="daisy-skeleton h-10 flex-1 rounded-lg"></div>
            </div>
          </div>
        </div>
      }
    </div>
  } @else if (filteredCourses.length > 0) {
    <!-- Courses Grid (Instructor Panel Card Design) -->
    <div class="grid gap-8 md:grid-cols-2 lg:grid-cols-3">
      @for (course of filteredCourses; track course._id) {
        <div
          class="bg-base-100 border border-base-300 rounded-2xl shadow-xl overflow-hidden hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-1 flex flex-col h-full"
        >
          <!-- Course Banner -->
          <div
            class="relative h-48 overflow-hidden bg-gradient-to-br from-blue-600 to-purple-600 shrink-0"
          >
            @if (course.settings.bannerImageResource) {
              <img
                [src]="getBannerUrl(course.settings.bannerImageResource)"
                class="w-full h-full object-cover opacity-90"
                [alt]="course.title"
              />
            } @else {
              <div
                class="w-full h-full flex items-center justify-center text-white/40 text-4xl"
              >
                <i class="fa-solid fa-graduation-cap"></i>
              </div>
            }

            <!-- Status Badge -->
            <div class="absolute top-4 right-4">
              <span
                class="daisy-badge daisy-badge-sm font-medium"
                [ngClass]="{
                  'daisy-badge-warning': course.status === 'UNDER_REVIEW',
                  'daisy-badge-success': course.status === 'PUBLISHED',
                  'daisy-badge-info': course.status === 'DRAFT' || !course.status,
                  'daisy-badge-ghost': course.status === 'ARCHIVED',
                }"
              >
                {{ course.status || 'DRAFT' }}
              </span>
            </div>

            <!-- Audience Badge -->
            @if (course.targetAudience) {
              <div class="absolute top-4 left-4">
                <span
                  class="daisy-badge daisy-badge-primary daisy-badge-sm font-semibold uppercase tracking-wider shadow-sm"
                >
                  {{ course.targetAudience }}
                </span>
              </div>
            }
          </div>

          <!-- Course Content -->
          <div class="p-6 flex-1 flex flex-col justify-between gap-4">
            <div>
              @if (course.subject) {
                <div
                  class="text-xs font-bold text-primary uppercase tracking-wider mb-1"
                >
                  {{ course.subject }}
                </div>
              }

              <h3 class="text-xl font-bold text-base-content mb-2 line-clamp-2">
                {{ course.title }}
              </h3>

              <p class="text-base-content/70 text-sm line-clamp-2">
                {{
                  course.details.descriptionSummary ||
                    course.details.description ||
                    'Manage curriculum, lessons, and course materials'
                }}
              </p>

              <!-- Meta Info -->
              <div
                class="flex flex-wrap items-center gap-3 pt-3 text-xs text-base-content/70 border-t border-base-200 mt-3"
              >
                <span class="inline-flex items-center gap-1.5" title="Modules">
                  <i class="fa-solid fa-layer-group text-primary text-xs"></i>
                  <span>{{ course.contents.length || 0 }} Modules</span>
                </span>
                <span class="inline-flex items-center gap-1.5" title="Lessons">
                  <i class="fa-solid fa-circle-play text-primary text-xs"></i>
                  <span>{{ getTotalLessons(course) }} Lessons</span>
                </span>
                <span class="inline-flex items-center gap-1.5" title="Price">
                  <i class="fa-solid fa-tag text-primary text-xs"></i>
                  <span class="font-semibold">{{
                    formatPrice(course.settings.price)
                  }}</span>
                </span>
              </div>
            </div>

            <!-- Action Buttons -->
            <div class="flex gap-3 mt-auto pt-2">
              <button
                (click)="previewCourse(course._id)"
                class="flex-1 daisy-btn daisy-btn-primary gap-2 shadow-md hover:shadow-lg transition-all"
              >
                <i class="fa-solid fa-eye"></i>
                <span class="hidden sm:inline">Preview</span>
              </button>
              <button
                class="flex-1 daisy-btn daisy-btn-outline gap-2 shadow-md hover:shadow-lg transition-all"
                (click)="editCourse(course._id)"
              >
                <i class="fa-solid fa-cog"></i>
                <span class="hidden sm:inline">Manage</span>
              </button>
              <button
                type="button"
                (click)="openInviteModal(course._id)"
                class="daisy-btn daisy-btn-outline daisy-btn-square shadow-md hover:shadow-lg transition-all"
                title="Invite Students"
              >
                <i class="fa-solid fa-user-plus"></i>
              </button>
            </div>
          </div>
        </div>
      }
    </div>
  } @else {
    <!-- Empty State (Instructor Panel Style) -->
    <div class="text-center py-16">
      <div class="mb-8">
        <div
          class="w-24 h-24 bg-base-200 rounded-full flex items-center justify-center mx-auto mb-4"
        >
          <i
            class="fa-solid fa-graduation-cap text-4xl text-base-content/40"
          ></i>
        </div>
        <h3 class="text-2xl font-bold text-base-content mb-2">
          No Courses Yet
        </h3>
        <p class="text-base-content/70 max-w-md mx-auto">
          @if (searchQuery || selectedStatus !== 'ALL') {
            No courses match your active search and filter criteria. Try clearing the filters.
          } @else {
            Get started by creating your first course. You'll be able to
            structure curriculum modules, upload video lectures, and share study materials.
          }
        </p>
      </div>
      <div class="flex items-center justify-center gap-3">
        @if (searchQuery || selectedStatus !== 'ALL') {
          <button
            type="button"
            (click)="searchQuery = ''; selectedStatus = 'ALL'; applyFilters()"
            class="daisy-btn daisy-btn-outline daisy-btn-md"
          >
            Reset Filters
          </button>
        }
        <button
          class="daisy-btn daisy-btn-primary daisy-btn-lg gap-2 shadow-lg hover:shadow-xl transition-shadow"
          (click)="createCourse()"
        >
          <i class="fa-solid fa-plus"></i>
          Create Your First Course
        </button>
      </div>
    </div>
  }
</div>

<!-- Reusable Invite Modal -->
<black-invite-student-modal
  [isOpen]="showInviteModal"
  [availableCourses]="courses"
  [defaultCourseId]="selectedCourseIdForInvite"
  (close)="showInviteModal = false"
></black-invite-student-modal>
